import { Issue, Project } from '@/types';

// Project colors for consistent coloring
export const PROJECT_COLORS = [
  'bg-primary', 'bg-green-500', 'bg-yellow-500', 'bg-red-500',
  'bg-purple-500', 'bg-pink-500', 'bg-indigo-500', 'bg-blue-400',
  'bg-orange-500', 'bg-teal-500', 'bg-cyan-500', 'bg-lime-500'
];

export interface ProjectInfo {
  key: string;
  name: string;
  color: string;
}

export interface ProjectBreakdownData {
  storyPoints: number;
  completedStoryPoints: number;
}

export function getProjectKey(issue: Issue): string {
  return issue.key.split('-')[0];
}

export function createProjectMap(issues: Issue[], projects: Project[]): Map<string, ProjectInfo> {
  const projectsMap = new Map<string, ProjectInfo>();
  
  // First pass: collect all projects and their story points
  const projectStoryPoints = new Map<string, number>();
  
  issues.forEach(issue => {
    if (!issue.storyPoints) return;
    
    // Extract project key from issue key (e.g., "ABC-123" -> "ABC")
    const projectKey = getProjectKey(issue);
    const currentPoints = projectStoryPoints.get(projectKey) || 0;
    projectStoryPoints.set(projectKey, currentPoints + issue.storyPoints);
  });
  
  // Get all project keys with story points and sort them alphabetically
  const projectKeys = Array.from(projectStoryPoints.entries())
    .filter(([, points]) => points > 0)
    .map(([key]) => key)
    .sort();
  
  // Create projects map with colors based on sorted index
  projectKeys.forEach((projectKey, index) => {
    // Find project name from projects atom
    const projectInfo = projects.find(p => p.key === projectKey);
    
    projectsMap.set(projectKey, {
      key: projectKey,
      name: projectInfo?.name || projectKey,
      color: PROJECT_COLORS[index % PROJECT_COLORS.length]
    });
  });

  return projectsMap;
}

export function getProjectBreakdown(issues: Issue[]): Map<string, ProjectBreakdownData> {
  const projectBreakdown = new Map<string, ProjectBreakdownData>();

  issues.forEach(issue => {
    if (!issue.storyPoints) return;

    const projectKey = getProjectKey(issue);
    if (!projectBreakdown.has(projectKey)) {
      projectBreakdown.set(projectKey, {
        storyPoints: 0,
        completedStoryPoints: 0
      });
    }

    const project = projectBreakdown.get(projectKey);
    if (project) {
      project.storyPoints += issue.storyPoints;
      if (issue.done) {
        project.completedStoryPoints += issue.storyPoints;
      }
    }
  });

  return projectBreakdown;
}